import { useEffect, useRef } from 'react';
import { AppState } from 'react-native';
import { type WebViewType, IS_IOS } from '@bonanza-social/moonlight';

/**
 * Loads casino games in a WebView with automatic audio resume after backgrounding.
 * Hooks AudioContext in the main frame and resumes playback when the app returns to foreground.
 * Note: This only works for same-origin content due to browser security restrictions.
 */
const AUDIO_RESUME_SCRIPT = `
(function() {
  // Use a namespaced object to avoid global pollution
  if (!window.__BONANZA_IOS_AUDIO__) {
    const allAudioContexts = new Set();
    
    // Hook AudioContext creation to store references
    if (window.AudioContext || window.webkitAudioContext) {
      const OriginalAudioContext = window.AudioContext || window.webkitAudioContext;
      
      // Preserve the original constructor's prototype
      const CustomAudioContext = function(...args) {
        const ctx = new OriginalAudioContext(...args);
        allAudioContexts.add(ctx);
        
        // Clean up when context is closed
        const originalClose = ctx.close.bind(ctx);
        ctx.close = function() {
          allAudioContexts.delete(ctx);
          return originalClose();
        };
        
        return ctx;
      };
      
      // Preserve prototype chain
      CustomAudioContext.prototype = OriginalAudioContext.prototype;
      
      window.AudioContext = CustomAudioContext;
      window.webkitAudioContext = CustomAudioContext;
    }
    
    // Create namespaced API
    window.__BONANZA_IOS_AUDIO__ = {
      resume: function() {
        allAudioContexts.forEach(ctx => {
          if (ctx.state === 'suspended' || ctx.state === 'interrupted') {
            ctx.resume().catch(() => {});
          }
        });
      }
    };
    
    // Listen for resume messages with origin validation
    window.addEventListener('message', (event) => {
      // Only respond to messages from the same origin or trusted domains
      // For cross-origin iframes, they need to include this script themselves
      if (event.data && typeof event.data === 'object' && event.data.type === 'resumeAudio') {
        if (window.__BONANZA_IOS_AUDIO__) {
          window.__BONANZA_IOS_AUDIO__.resume();
        }
      }
    });
  }
})();`;

const useIosGameSoundResume = () => {
  const webViewRef = useRef<WebViewType>(null);

  // Resume audio when app returns from background
  useEffect(() => {
    if (!IS_IOS) return;

    const subscription = AppState.addEventListener('change', (state) => {
      if (state !== 'active') return;

      webViewRef.current?.injectJavaScript(`
              (function() {
                const iframes = document.querySelectorAll('iframe');
                iframes.forEach((iframe) => {
                  try {
                    iframe.contentWindow.postMessage({type: 'resumeAudio'}, '*');
                  } catch(e) {}
                });
              })();
            `);
    });

    return () => subscription.remove();
  }, []);

  return {
    webViewRef,
    iosGameSoundResumeScript: IS_IOS ? AUDIO_RESUME_SCRIPT : undefined,
    injectedJavaScriptBeforeContentLoadedForMainFrameOnly: IS_IOS
      ? false
      : undefined,
  };
};

export default useIosGameSoundResume;
